import axios from 'axios';

/*
 * 短期票据（X-Ticket）接入模块
 * ----------------------------------------------------------
 * 背景：后端给所有查询接口加了认证，但浏览器里不能放长期密钥，
 *       所以改为「先取一张短期票，之后每次业务请求带上这张票」。
 *       票据 300 秒过期、单票 60 次配额、绑定签发 IP，
 *       用完了/过期了后端返回明确错误码，本模块自动换票 + 重放一次。
 *
 * 对业务代码透明：任何通过 http.js 的请求无需手动传参。
 *
 * 注意路径写法：本工程 baseURL（config.json 的 ip）通常已带 /v1，
 *   所以这里一律使用前端相对路径（如 /auth/ticket），
 *   对应后端文档里的 /v1/auth/ticket —— 两者指向同一接口。
 */

// 票据只存内存：不放 localStorage / sessionStorage（避免泄漏面）
let ticket = null;
// 单例 Promise：并发请求只会触发一次取票（防止首屏 8 个请求打 8 次取票接口）
let pendingFetch = null;

// 取票接口（前端相对路径，对应后端 POST /v1/auth/ticket，免登录、不带任何认证头）
const TICKET_PATH = '/auth/ticket';

// 不需要带票的路径（前缀匹配，采用「全局加票 + 排除名单」策略，以后新增接口无需再改）
const EXCLUDE_PREFIXES = [
  '/auth/ticket',      // 取票接口本身免认证，绝不能带 X-Ticket（否则可能自我循环）
  '/user/login',       // 登录接口免认证
  '/docs',             // 接口文档（静态）
  '/openapi.json',     // OpenAPI 定义（静态）
  '/media',            // 后端静态媒体资源（静态）
];

/** 判断该请求是否需要带票 */
function needTicket(url = '') {
  return !EXCLUDE_PREFIXES.some((p) => url.startsWith(p));
}

/**
 * 兜底：确保 axios.defaults.baseURL 可用
 * 正常情况下 main.js 启动时已从 /data/config.json 设置过 baseURL；
 * 若因某种原因（配置加载失败/时序问题）没设置上，在取票前补读一次 ip 作为 baseURL。
 * 用原生 fetch 直读（相对路径走页面同源），
 * 避免再经过 axios 拦截器造成「取票 ↔ 读配置」循环。
 * 兜底失败时不抛出：后续取票请求会失败并把真实错误抛给调用方，保持链路可观测。
 */
let baseURLPromise = null;   // 单例 promise：并发场景只读一次配置
function ensureBaseURL() {
  if (axios.defaults.baseURL) return Promise.resolve();   // main.js 已设置，无需兜底
  if (!baseURLPromise) {
    baseURLPromise = (async () => {
      try {
        const res = await fetch('/data/config.json');
        const data = await res.json();
        if (data && data.ip) {
          axios.defaults.baseURL = data.ip;
          // 兜底补设成功：之后所有相对路径请求都会拼上这个前缀
        }
      } catch (e) {
        // 读取失败：静默，让后续取票/请求自行暴露错误
      }
    })();
  }
  return baseURLPromise;
}

/**
 * 取票（并发安全：多个请求同时发现没票时，只会发一次请求）
 * @returns {Promise<string>} 票据字符串
 */
export function fetchTicket() {
  if (ticket) return Promise.resolve(ticket);
  if (!pendingFetch) {
    pendingFetch = axios
      .post(TICKET_PATH)                // 不带任何自定义头/请求体，保持简单请求
      .then((res) => {
        ticket = res.data.ticket;
        return ticket;
      })
      .finally(() => {
        pendingFetch = null;            // 无论成功失败都释放，避免永久卡住
      });
  }
  return pendingFetch;
}

/** 清票（票据失效时调用，下次请求会自动重新取票） */
export function clearTicket() {
  ticket = null;
}

// ============ 请求拦截器：没票先取票，然后带上 X-Ticket ============
axios.interceptors.request.use(async (config) => {
  if (needTicket(config.url || '')) {
    if (!ticket) {
      await ensureBaseURL();    // 兜底：main.js 没设置 baseURL 时补设，避免取票打到页面本身
      await fetchTicket();      // 并发场景下后续请求自动复用进行中的取票
    }
    if (ticket) {
      config.headers['X-Ticket'] = ticket;
    }
  }
  return config;
});

// ============ 响应拦截器：票据类错误 → 换票 → 重放一次 ============
axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error;
    const code = response?.data?.detail?.code || '';
    // 约定一条规则：detail.code 以 TICKET_ 开头、或者是 AUTH_REQUIRED → 换票重放
    const isTicketError =
      code === 'AUTH_REQUIRED' || code.startsWith('TICKET_');

    // 关键：只重放一次，防止后端持续 401 时死循环 + 无限取票。
    // 排除名单内的请求（取票/登录等）出错也不重放，避免自我循环。
    if (isTicketError && config && needTicket(config.url || '') && !config.__ticketRetried) {
      config.__ticketRetried = true;
      clearTicket();
      try {
        await fetchTicket();
      } catch (e) {
        return Promise.reject(error);   // 取票也失败了，把原始错误抛出去（不无限重试）
      }
      config.headers['X-Ticket'] = ticket;
      return axios(config);             // 重放原请求
    }

    return Promise.reject(error);
  }
);